<script setup lang="ts">
import { Color } from '@previo/codebooks'
import { QItem, QItemSection } from 'quasar'
import { computed, ref, watch } from 'vue'
import { Dayjs } from 'dayjs'
import { $t } from '../../../../composables/i18n'
import PDivider from '../../../divider/PDivider.vue'
import PList from '../../../list/PList.vue'
import PTextFormBody from '../../../typography/form/PTextFormBody.vue'
import { PDatePickerFastChoice, type PDatePickerFastChoiceItem } from '../../../date-picker/types'
import { dateFastChoicesList } from '../../../date-picker/constants'
import { applyDefaultTime, resolveDateFastChoice } from '../../../date-picker/utils'
import { useDateTimePicker } from '../../composables/DateTimePickerState'

type Props = {
  fastChoices?: PDatePickerFastChoice[]
  defaultTime?: string
}

type Emits = {
  (e: 'select'): void
}

const props = defineProps<Props>()
const emits = defineEmits<Emits>()
const model = defineModel<Dayjs | null>()

const navigation = useDateTimePicker()

const selectedFastChoice = ref<PDatePickerFastChoice>()
const visibleFastChoice = computed<PDatePickerFastChoiceItem[]>(() => {
  const visible: PDatePickerFastChoiceItem[] = []

  props.fastChoices?.map((choiceType) => {
    const choice = dateFastChoicesList.find((choice) => choice.type === choiceType)

    if (choice) {
      visible.push(choice)
    }
  })

  return visible
})

const hasFastChoice = computed<boolean>(() => {
  return visibleFastChoice.value.length > 0
})

watch(selectedFastChoice, (value) => {
  const date = resolveDateFastChoice(value).tz()
  const dateWithTime = model.value
    ? date.withTimeFrom(model.value)
    : applyDefaultTime(date, props.defaultTime)

  model.value = dateWithTime
  navigation.setDate(dateWithTime)
  emits('select')
})
</script>

<template>
  <div v-if="hasFastChoice" class="ds-date-time-picker__fast-choices-list">
    <template v-for="choice in visibleFastChoice" :key="choice.type">
      <p-list dense>
        <q-item
          active-class="text-primary"
          :active="selectedFastChoice === choice.type"
          clickable
          @click="selectedFastChoice = choice.type"
        >
          <q-item-section class="p-cursor-pointer">
            <div class="p-flex">
              <p-text-form-body
                :text="$t(choice.name)"
                :color="selectedFastChoice === choice.type ? Color.PRIMARY : undefined"
              />
            </div>
          </q-item-section>
        </q-item>
      </p-list>
      <p-divider />
    </template>
  </div>
</template>

<style lang="scss" scoped>
.ds-date-time-picker {
  &__fast-choices-list {
    height: 350px;
    overflow-y: auto;
  }
}
</style>
